/**
 * Console shell — the AVOS SaaS operator console layout (brand §09).
 *
 * A production-grade admin frame:
 *   - a full-width TOP BAR carrying the AVOS lockup (arch mark + wordmark) on
 *     the left, the current section title, and a right cluster with a
 *     notifications bell and a profile menu — each a small anchored dropdown;
 *   - a persistent, FULL-HEIGHT left SIDEBAR listing every module with an icon
 *     and label, the active item highlighted (`aria-current`). It collapses to
 *     an icon-only rail on desktop (toggle in the top bar) and becomes an
 *     off-canvas drawer with a backdrop under 900px;
 *   - a focusable, scrollable MAIN region (`role="main"`).
 *
 * Navigation is client-side via the History API (pushState + popstate).
 * Accessibility: skip link, `aria-current` on the active item, `aria-expanded`
 * on toggles, visible focus, operable at 360px.
 *
 * SSR-safe: the initial render is a pure function of `initialPath`; all
 * browser-API access is inside effects/handlers, guarded for the server path.
 */
import {
  useCallback,
  useEffect,
  useState,
  type MouseEvent,
  type ReactElement,
} from "react";
import { MeProvider, useMe, AccessDenied, goToLoginOnce, type MeUser } from "./useMe.js";
import UsersAccessScreen from "./screens/UsersAccessScreen.js";
import {
  CONSOLE_MODULES,
  MODULE_PERMISSION,
  LANDING_ORDER,
  matchModule,
  matchSettingsSection,
  type ModuleKey,
  type SettingsSectionKey,
} from "./modules.js";
import { ArchMark } from "./ArchMark.js";
import { Dropdown } from "./Dropdown.js";
import { BookingComposer } from "./screens/BookingScreen.js";
import { apiListProperties, apiListBookings, apiLogout, formatMoney, type Property } from "./services.js";
import {
  loadBookingNotifications,
  markBookingNotificationRead,
  markAllBookingNotificationsRead,
  relativeTime,
  shortDate,
  type BookingNotification,
} from "./bookingNotifications.js";
import { isModuleAvailable } from "./moduleAccess.js";
import {
  ModuleIcon,
  BellIcon,
  MenuIcon,
  ChevronDownIcon,
  SignOutIcon,
} from "./icons.js";
import DashboardScreen from "./screens/DashboardScreen.js";
import PropertiesScreen from "./screens/PropertiesScreen.js";
import BookingsScreen from "./screens/BookingsScreen.js";
import ChannelsScreen from "./screens/ChannelsScreen.js";
import ReconciliationScreen from "./screens/ReconciliationScreen.js";
import RevenueScreen, { InventoryRatesScreen } from "./screens/RevenueScreen.js";
import AutomationScreen from "./screens/AutomationScreen.js";
import OperationsScreen from "./screens/OperationsScreen.js";
import MarketingScreen from "./screens/MarketingScreen.js";
import CrmScreen from "./screens/CrmScreen.js";
import AgentsScreen from "./screens/AgentsScreen.js";
import AnalyticsScreen from "./screens/AnalyticsScreen.js";
import AccountsScreen from "./screens/AccountsScreen.js";
import AdminScreen from "./screens/AdminScreen.js";
import PlaceholderScreen from "./screens/PlaceholderScreen.js";
// Authenticated account functions, reused under /app/settings/* (no redesign).
import AccountScreen from "../screens/AccountScreen.js";
import RoleManagementScreen from "../screens/RoleManagementScreen.js";
import ImpersonationConsentScreen from "../screens/ImpersonationConsentScreen.js";
import DataExportScreen from "../screens/DataExportScreen.js";
import AccountDeletionScreen from "../screens/AccountDeletionScreen.js";
import MfaEnrollmentScreen from "../screens/MfaEnrollmentScreen.js";

/**
 * Display identity for the top bar / profile menu, derived from the TRUSTED
 * authenticated session (/api/auth/me via {@link useMe}). No demo/placeholder
 * identities: while /me is still loading we show neutral fallbacks, never a
 * fake name or email.
 */
interface DisplayIdentity {
  readonly name: string;
  readonly email: string;
  readonly role: string;
  readonly org: string;
  readonly ready: boolean;
}
function displayIdentity(user: MeUser | null): DisplayIdentity {
  if (user === null) {
    return { name: "Account", email: "", role: "", org: "", ready: false };
  }
  const full = [user.firstName, user.lastName].filter(Boolean).join(" ").trim();
  const name = full !== "" ? full : user.email;
  return {
    name,
    email: user.email,
    role: user.role ?? "",
    org: user.organisationName ?? "",
    ready: true,
  };
}

/**
 * Sidebar information architecture (frontend-only): Dashboard stays a standalone
 * first item; the remaining modules are grouped under subtle, non-clickable
 * section labels around hospitality workflows. This ONLY controls display order
 * + grouping in the sidebar — routes, keys, icons and {@link CONSOLE_MODULES}
 * (used by routing/`matchModule`) are unchanged. Each entry references an
 * existing {@link ModuleKey}; a `null` section is a standalone item (Dashboard).
 */
export const NAV_GROUPS: readonly {
  readonly section: string | null;
  readonly keys: readonly ModuleKey[];
}[] = [
  { section: null, keys: ["dashboard"] },
  { section: "Property & Bookings", keys: ["properties", "bookings", "inventory"] },
  { section: "Distribution & Revenue", keys: ["channels", "revenue"] },
  { section: "Guest & Operations", keys: ["crm", "operations", "automation"] },
  { section: "Finance", keys: ["reconciliation", "accounts"] },
  { section: "Growth & Intelligence", keys: ["marketing", "analytics", "agents"] },
  { section: "System", keys: ["admin"] },
];

// The bell now surfaces ONLY new-booking notifications, DERIVED from the shared
// persisted reservation list (provider-agnostic). The former demo entries
// (payouts / channel sync / tasks overdue) were removed. See
// ./bookingNotifications for the derivation + dedupe (one persisted reservation
// = one notification) and read-state handling.

/** Render the active module screen. `dataVersion` bumps to force a reload
 *  (e.g. after a new booking) by remounting the data-driven dashboard. */
function renderModule(key: ModuleKey, dataVersion: number): ReactElement {
  switch (key) {
    case "dashboard":
      return <DashboardScreen key={`dashboard-${dataVersion}`} />;
    case "properties":
      return <PropertiesScreen />;
    case "bookings":
    case "booking":
      // Direct Booking merged into the unified Bookings register.
      return <BookingsScreen key={`bookings-${dataVersion}`} />;
    case "channels":
      return <ChannelsScreen />;
    case "reconciliation":
      return <ReconciliationScreen />;
    case "inventory":
      return <InventoryRatesScreen />;
    case "revenue":
      return <RevenueScreen />;
    case "automation":
      return <AutomationScreen />;
    case "operations":
      return <OperationsScreen />;
    case "marketing":
      return <MarketingScreen />;
    case "crm":
      return <CrmScreen />;
    case "agents":
      return <AgentsScreen />;
    case "analytics":
      return <AnalyticsScreen />;
    case "accounts":
      return <AccountsScreen key={`accounts-${dataVersion}`} />;
    case "admin":
      return <AdminScreen />;
    default: {
      const mod = CONSOLE_MODULES.find((m) => m.key === key);
      return (
        <PlaceholderScreen
          title={mod?.label ?? "Module"}
          description={mod?.description ?? ""}
        />
      );
    }
  }
}

/**
 * Render an authenticated account/settings sub-screen (under /app/settings/*)
 * inside the app shell. Reuses the existing account screens verbatim — this is
 * route/shell integration only, no screen redesign:
 *   profile → Account · security → MFA enrolment · team → Role management ·
 *   support-access → Impersonation consent · data-privacy → Export + Delete.
 */
function renderSettingsSection(section: SettingsSectionKey): ReactElement {
  switch (section) {
    case "users":
      return <UsersAccessScreen />;
    case "profile":
      return <AccountScreen />;
    case "security":
      return <MfaEnrollmentScreen />;
    case "team":
      return <RoleManagementScreen />;
    case "support-access":
      return <ImpersonationConsentScreen />;
    case "data-privacy":
      return (
        <>
          <DataExportScreen />
          <AccountDeletionScreen />
        </>
      );
    default:
      return <AdminScreen />;
  }
}

/** Initials for the profile avatar (deterministic, SSR-safe). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "U";
}

/** The sidebar navigation listing all modules with icons. */
function Sidebar({
  activePath,
  onNavigate,
  isDrawerOpen,
  isCollapsed,
  onClose,
}: {
  readonly activePath: string;
  readonly onNavigate: (path: string) => void;
  readonly isDrawerOpen: boolean;
  readonly isCollapsed: boolean;
  readonly onClose: () => void;
}): ReactElement {
  const me = useMe();
  const cls = [
    "console-sidebar",
    isDrawerOpen ? "console-sidebar--open" : "",
    isCollapsed ? "console-sidebar--collapsed" : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <nav aria-label="Console modules" className={cls} data-testid="console-sidebar">
      {/* Brand lockup at the top of the full-height sidebar. */}
      <a
        href="/app/dashboard"
        className="console-sidebar__brand"
        aria-label="AVOS — go to dashboard"
        title={isCollapsed ? "AVOS" : undefined}
        onClick={(e: MouseEvent<HTMLAnchorElement>) => {
          if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey) {
            e.preventDefault();
            onNavigate("/app/dashboard");
            onClose();
          }
        }}
      >
        <ArchMark size={28} baselineColor="#101828" />
        <span className="console-sidebar__brand-word">
          <span className="console-sidebar__brand-name">AVOS</span>
          <span className="console-sidebar__brand-endorse">
            A Midhurst Hospitality System
          </span>
        </span>
      </a>
      <ul className="console-sidebar__list">
        {NAV_GROUPS.map((group) => {
          const mods = group.keys
            .map((key) => CONSOLE_MODULES.find((m) => m.key === key))
            .filter((m): m is (typeof CONSOLE_MODULES)[number] => m !== undefined)
            // Module-release gate: hide modules disabled in this environment
            // (AVOS_ENABLED_MODULES). Same source of truth the server uses to
            // gate the routes/APIs. Empty groups collapse below.
            .filter((m) => isModuleAvailable(m.key))
            // Permission-aware sidebar: show a module only when its gating
            // permission is held (admin is always shown for personal settings).
            .filter((m) => {
              const perm = MODULE_PERMISSION[m.key];
              return perm === null || me.can(perm) || !me.user; // show all pre-load / demo
            });
          if (mods.length === 0) return null;
          return (
            <li key={group.section ?? "root"} className="console-sidebar__group">
              {group.section !== null ? (
                <p
                  className="console-sidebar__section"
                  aria-hidden="true"
                  title={isCollapsed ? group.section : undefined}
                >
                  {group.section}
                </p>
              ) : null}
              <ul className="console-sidebar__sublist">
                {mods.map((mod) => {
                  const isActive = mod.path === activePath;
                  return (
                    <li key={mod.path}>
                      <a
                        href={mod.path}
                        className={
                          isActive
                            ? "console-sidebar__link console-sidebar__link--active"
                            : "console-sidebar__link"
                        }
                        title={isCollapsed ? mod.label : undefined}
                        {...(isActive ? { "aria-current": "page" as const } : {})}
                        onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                          if (
                            e.button === 0 &&
                            !e.metaKey &&
                            !e.ctrlKey &&
                            !e.shiftKey &&
                            !e.altKey
                          ) {
                            e.preventDefault();
                            onNavigate(mod.path);
                            onClose();
                          }
                        }}
                      >
                        <span className="console-sidebar__icon" aria-hidden="true">
                          <ModuleIcon moduleKey={mod.key} />
                        </span>
                        <span className="console-sidebar__label">{mod.label}</span>
                      </a>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** The notifications dropdown panel contents. */
/** One compact summary line for a booking notification. */
function notifSummary(n: BookingNotification): string {
  const ref = n.reference !== null ? ` \u00b7 ${n.reference}` : "";
  return `${n.guestName}${ref}`;
}

function notifWhereLine(n: BookingNotification): string {
  const place = n.propertyName ?? "Property";
  const dates = `${shortDate(n.checkIn)} \u2192 ${shortDate(n.checkOut)}`;
  return `${place} \u00b7 ${dates}`;
}

function notifSourceLine(n: BookingNotification): string {
  const amount =
    n.amountMinor !== null ? ` \u00b7 ${formatMoney(Number(n.amountMinor), n.currency)}` : "";
  return `${n.source}${amount}`;
}

/**
 * The notifications dropdown — booking notifications only. Clicking an item
 * marks it read and deep-links to the Bookings page with the reservation
 * pre-selected (the existing drawer auto-opens). Empty state when none.
 */
function NotificationsPanel({
  notifications,
  onOpenBooking,
  onMarkAllRead,
}: {
  readonly notifications: readonly BookingNotification[];
  readonly onOpenBooking: (reservationId: string) => void;
  readonly onMarkAllRead: () => void;
}): ReactElement {
  const unread = notifications.filter((n) => !n.read).length;
  return (
    <>
      <div className="console-menu__header">
        <span className="console-menu__title">Notifications</span>
        {unread > 0 ? <span className="console-menu__count">{unread}</span> : null}
      </div>
      {notifications.length === 0 ? (
        <p className="console-notif__empty">No new booking notifications</p>
      ) : (
        <ul className="console-notif__list">
          {notifications.map((n) => (
            <li
              key={n.reservationId}
              className={n.read ? "console-notif console-notif--read" : "console-notif"}
            >
              <button
                type="button"
                className="console-notif__link"
                data-dropdown-dismiss
                onClick={() => onOpenBooking(n.reservationId)}
              >
                <span
                  className={`console-notif__dot console-notif__dot--${n.read ? "ok" : "attention"}`}
                  aria-hidden="true"
                />
                <span className="console-notif__body">
                  <span className="console-notif__title">New booking</span>
                  <span className="console-notif__detail">{notifSummary(n)}</span>
                  <span className="console-notif__detail">{notifWhereLine(n)}</span>
                  <span className="console-notif__detail">{notifSourceLine(n)}</span>
                  <span className="console-notif__when">{relativeTime(n.createdAt)}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {notifications.length > 0 && unread > 0 ? (
        <button
          type="button"
          className="console-menu__footer-action"
          onClick={onMarkAllRead}
        >
          Mark all as read
        </button>
      ) : null}
    </>
  );
}

/** The top-bar profile button contents — avatar + name/role from the session. */
export function ProfileTrigger(): ReactElement {
  const me = useMe();
  const who = displayIdentity(me.user);
  return (
    <>
      <span className="console-avatar" aria-hidden="true">
        {initials(who.name)}
      </span>
      <span className="console-profilebtn__meta">
        <span className="console-profilebtn__name">{who.name}</span>
        {who.role !== "" ? (
          <span className="console-profilebtn__role">{who.role}</span>
        ) : null}
      </span>
      <span className="console-profilebtn__chevron" aria-hidden="true">
        <ChevronDownIcon />
      </span>
    </>
  );
}

/**
 * The profile dropdown panel contents.
 *
 * Identity comes from the TRUSTED session (`useMe`); there are no demo values.
 * "Sign out" is a real, server-backed logout: it POSTs to /api/auth/logout
 * (which revokes the CURRENT persistent_session and clears the auth cookie),
 * then — only after the server confirms — redirects to /login. It is NOT a
 * client-only `window.location = "/login"`. The button disables itself while
 * the request is in flight (so a double-click cannot fire two logouts) and
 * surfaces a retry message if the request fails, so the UI never falsely claims
 * revocation that did not happen.
 */
export function ProfilePanel(): ReactElement {
  const me = useMe();
  const who = displayIdentity(me.user);
  const [signingOut, setSigningOut] = useState(false);
  const [signOutError, setSignOutError] = useState(false);

  async function handleSignOut(): Promise<void> {
    if (signingOut) return; // idempotent against double-click
    setSigningOut(true);
    setSignOutError(false);
    const ok = await apiLogout();
    if (ok) {
      // Server confirmed revocation + cookie clear. Navigate to /login via the
      // SHARED one-shot guard so this redirect and any concurrent MeProvider
      // 401-redirect collapse into a SINGLE navigation (fixes the reload loop).
      // A full-document navigation also drops all cached authenticated state,
      // and `replace` keeps the dead /app entry out of history.
      goToLoginOnce();
      return;
    }
    // Network/server failure: do NOT claim we signed out. Re-enable + show retry.
    setSigningOut(false);
    setSignOutError(true);
  }

  return (
    <>
      <div className="console-menu__profile">
        <span className="console-avatar console-avatar--lg" aria-hidden="true">
          {initials(who.name)}
        </span>
        <span className="console-menu__profile-meta">
          <span className="console-menu__profile-name">{who.name}</span>
          {who.email !== "" ? (
            <span className="console-menu__profile-email">{who.email}</span>
          ) : null}
          {who.role !== "" || who.org !== "" ? (
            <span className="console-menu__profile-role">
              {[who.role, who.org].filter(Boolean).join(" · ")}
            </span>
          ) : null}
        </span>
      </div>
      <div className="console-menu__sep" role="separator" />
      <a
        href="/app/settings/profile"
        className="console-menu__item"
        data-dropdown-dismiss
      >
        Account &amp; settings
      </a>
      <a
        href="/app/settings/team"
        className="console-menu__item"
        data-dropdown-dismiss
      >
        Team &amp; roles
      </a>
      <div className="console-menu__sep" role="separator" />
      {signOutError ? (
        <p className="console-menu__signout-error" role="alert">
          Could not sign you out. Please try again.
        </p>
      ) : null}
      <button
        type="button"
        className="console-menu__item console-menu__item--danger console-menu__item--button"
        data-testid="console-signout"
        onClick={handleSignOut}
        disabled={signingOut}
        aria-busy={signingOut}
      >
        <span className="console-menu__item-icon" aria-hidden="true">
          <SignOutIcon />
        </span>
        {signingOut ? "Signing out…" : "Sign out"}
      </button>
    </>
  );
}


/**
 * Permission guard for a module: renders the module when permitted, an Access
 * Denied panel otherwise. While /me is still loading (or in demo mode) it
 * renders optimistically. Backend enforcement is the real control; this is UX.
 */
function ModuleGuard({ moduleKey, dataVersion }: { readonly moduleKey: ModuleKey; readonly dataVersion: number }): ReactElement {
  const me = useMe();
  // Module-release gate (defense-in-depth; the server already redirects a
  // disabled module's route to the dashboard). If somehow rendered, show a
  // clean "not available" state rather than the module.
  if (!isModuleAvailable(moduleKey)) return <ModuleNotAvailable />;
  const perm = MODULE_PERMISSION[moduleKey];
  const allowed = perm === null || me.loading || !me.user || me.can(perm);
  if (!allowed) return <AccessDenied />;
  return renderModule(moduleKey, dataVersion);
}

/** Clean state shown when a module is not released in this environment. */
function ModuleNotAvailable(): ReactElement {
  return (
    <div className="access-denied" data-testid="module-not-available">
      <h1 className="access-denied__title">Module not available</h1>
      <p className="access-denied__body">This module is not available in this environment.</p>
      <p className="access-denied__hint">If you believe you should have access, contact your AVOS administrator.</p>
      <a className="access-denied__cta" href="/app/dashboard">Back to dashboard</a>
    </div>
  );
}

/** The console shell root. */
function ConsoleShellInner({
  initialPath,
}: {
  readonly initialPath: string;
}): ReactElement {
  const [path, setPath] = useState(initialPath);
  /** Mobile off-canvas drawer open state (under 900px). */
  const [drawerOpen, setDrawerOpen] = useState(false);
  /** Desktop icon-rail collapse state (>= 900px). */
  const [collapsed, setCollapsed] = useState(false);
  /** Whether the New Booking modal is open. */
  const [bookingOpen, setBookingOpen] = useState(false);
  /** Properties for the global New Booking composer (loaded lazily on open). */
  const [composerProperties, setComposerProperties] = useState<readonly Property[]>([]);
  /** Bumped after a booking so data-driven screens (dashboard) reload. */
  const [dataVersion, setDataVersion] = useState(0);

  // Booking notifications (bell). Derived from the shared reservation list +
  // localStorage dedupe/read state (provider-agnostic). Reloaded on mount and
  // whenever dataVersion bumps (e.g. after a New Booking via the composer).
  const [bookingNotifs, setBookingNotifs] = useState<readonly BookingNotification[]>([]);
  const refreshNotifications = useCallback(() => {
    let active = true;
    void apiListBookings({ limit: 50 })
      .then((res) => {
        if (!active) return;
        setBookingNotifs(loadBookingNotifications(res.rows));
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, []);

  const navigate = useCallback((next: string) => {
    if (typeof window !== "undefined") {
      window.history.pushState({}, "", next);
    }
    setPath(next);
    if (typeof document !== "undefined") {
      const main = document.getElementById("console-main");
      if (main !== null) {
        main.focus();
      }
    }
  }, []);

  useEffect(() => {
    const onPop = (): void => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // (Re)load booking notifications on mount + after each booking (dataVersion).
  useEffect(() => {
    const cleanup = refreshNotifications();
    return cleanup;
  }, [refreshNotifications, dataVersion]);

  // Open a booking from a notification: deep-link to Bookings with the
  // reservation id so the existing detail drawer auto-opens; mark it read.
  const openBookingFromNotification = useCallback((reservationId: string) => {
    markBookingNotificationRead(reservationId);
    setBookingNotifs((prev) =>
      prev.map((n) => (n.reservationId === reservationId ? { ...n, read: true } : n)),
    );
    navigate(`/app/bookings?booking=${encodeURIComponent(reservationId)}`);
  }, [navigate]);

  const markAllNotificationsRead = useCallback(() => {
    setBookingNotifs((prev) => {
      markAllBookingNotificationsRead(prev);
      return prev.map((n) => ({ ...n, read: true }));
    });
  }, []);

  const unreadBookingCount = bookingNotifs.filter((n) => !n.read).length;

  // Direct Booking was merged into the unified Bookings module: redirect the
  // legacy /app/booking route to /app/bookings so old links keep working.
  useEffect(() => {
    const normalized = path.split(/[?#]/)[0]?.replace(/\/+$/, "") ?? "";
    if (normalized === "/app/booking") {
      if (typeof window !== "undefined") {
        window.history.replaceState({}, "", "/app/bookings");
      }
      setPath("/app/bookings");
      return;
    }
    // IA reorg: the former Revenue & Rates operational tabs moved to Inventory &
    // Rates, and Sync Health moved to the Channel Manager. Redirect legacy
    // bookmarked "/app/revenue?tab=<operational>" URLs to their new home so no
    // link breaks. "overview" (and no tab) stays on the new Revenue page.
    if (normalized === "/app/revenue" && typeof window !== "undefined") {
      const tab = new URLSearchParams(window.location.search).get("tab");
      const toInventory = new Set(["inventory", "calendar", "rate-plans", "restrictions", "rules", "sync"]);
      let dest: string | null = null;
      if (tab !== null && toInventory.has(tab)) dest = "/app/inventory";
      if (dest !== null) {
        window.history.replaceState({}, "", dest);
        setPath(dest);
      }
    }
  }, [path]);

  // Load properties for the global New Booking composer when it first opens.
  useEffect(() => {
    if (!bookingOpen || composerProperties.length > 0) return;
    let live = true;
    apiListProperties()
      .then((ps) => { if (live) setComposerProperties(ps); })
      .catch(() => undefined);
    return () => { live = false; };
  }, [bookingOpen, composerProperties.length]);

  // Close the mobile drawer on Escape.
  useEffect(() => {
    if (!drawerOpen) {
      return;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        setDrawerOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawerOpen]);

  const activeModule = matchModule(path);
  // When the path is a /app/settings/* sub-section, render that account screen
  // in the main region (the sidebar still highlights "Admin & Settings").
  const settingsSection = matchSettingsSection(path);

  /** The sidebar toggle: collapses the rail on desktop, opens the drawer on mobile. */
  const onToggleNav = useCallback((): void => {
    if (typeof window !== "undefined" && window.innerWidth < 900) {
      setDrawerOpen((v) => !v);
    } else {
      setCollapsed((v) => !v);
    }
  }, []);

  const shellClass = [
    "console-shell",
    collapsed ? "console-shell--collapsed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      data-app-shell="avos-console"
      data-active-route={activeModule.path}
      className={shellClass}
    >
      <a href="#console-main" className="skip-link">
        Skip to main content
      </a>

      {/* Backdrop for the mobile drawer. */}
      {drawerOpen ? (
        <button
          type="button"
          className="console-scrim"
          aria-label="Close navigation"
          onClick={() => setDrawerOpen(false)}
        />
      ) : null}

      {/* Full-height sidebar (spans the whole window, top to bottom). */}
      <div id="console-sidebar-region">
        <Sidebar
          activePath={activeModule.path}
          onNavigate={navigate}
          isDrawerOpen={drawerOpen}
          isCollapsed={collapsed}
          onClose={() => setDrawerOpen(false)}
        />
      </div>

      {/* The column to the right of the sidebar: top bar + scrollable main. */}
      <div className="console-column">
        <header role="banner" className="console-topbar">
          <div className="console-topbar__left">
            <button
              type="button"
              className="console-iconbtn console-navtoggle"
              aria-expanded={drawerOpen || !collapsed}
              aria-controls="console-sidebar-region"
              aria-label="Toggle navigation"
              onClick={onToggleNav}
            >
              <MenuIcon />
            </button>
          </div>

          <div className="console-topbar__right">
            <button
              type="button"
              className="console-newbooking"
              onClick={() => setBookingOpen(true)}
            >
              <span aria-hidden="true">+</span> New booking
            </button>
            <Dropdown
              label="Notifications"
              panelRole="region"
              triggerClassName="console-iconbtn console-iconbtn--badge"
              panelClassName="console-menu console-menu--notif"
              trigger={
                <>
                  <BellIcon />
                  {unreadBookingCount > 0 ? (
                    <span className="console-iconbtn__badge" aria-hidden="true" />
                  ) : null}
                  <span className="console-sr-only">
                    {unreadBookingCount} new booking notifications
                  </span>
                </>
              }
            >
              <NotificationsPanel
                notifications={bookingNotifs}
                onOpenBooking={openBookingFromNotification}
                onMarkAllRead={markAllNotificationsRead}
              />
            </Dropdown>

            <Dropdown
              label="Your account"
              triggerClassName="console-profilebtn"
              panelClassName="console-menu console-menu--profile"
              trigger={<ProfileTrigger />}
            >
              <ProfilePanel />
            </Dropdown>
          </div>
        </header>

        <main id="console-main" role="main" tabIndex={-1} className="console-main">
          <div className="console-main__inner">
            {settingsSection !== null
              ? renderSettingsSection(settingsSection.key)
              : <ModuleGuard moduleKey={activeModule.key} dataVersion={dataVersion} />}
          </div>
        </main>
      </div>

      {bookingOpen ? (
        <BookingComposer
          properties={composerProperties}
          onClose={() => setBookingOpen(false)}
          onBooked={() => setDataVersion((v) => v + 1)}
        />
      ) : null}
    </div>
  );
}

/**
 * The console shell root, wrapped in the MeProvider so the sidebar, guards,
 * property selectors and action buttons can consult the authenticated user's
 * permissions + property access. After /me loads, if the user lacks
 * dashboard.view and is on the dashboard, redirect to their first accessible
 * module (§65).
 */
export function ConsoleShell(props: { readonly initialPath: string }): ReactElement {
  return (
    <MeProvider>
      <MustChangePasswordRedirect />
      <LandingRedirect />
      <ConsoleShellInner initialPath={props.initialPath} />
    </MeProvider>
  );
}

/**
 * Belt-and-suspenders first-login guard: if the authenticated user still owes a
 * mandatory password change and is somehow inside /app, send them to the
 * dedicated /change-password screen. The server already redirects /app/* and
 * 403s /api/* for a must-change user; this just keeps the client UX clean so a
 * must-change user never sees a half-loaded console.
 */
function MustChangePasswordRedirect(): ReactElement | null {
  const me = useMe();
  useEffect(() => {
    if (me.loading || !me.user || typeof window === "undefined") return;
    if (me.user.mustChangePassword && window.location.pathname !== "/change-password") {
      window.location.replace("/change-password");
    }
  }, [me.loading, me.user]);
  return null;
}

/** Redirect a user with no dashboard access to their first permitted module. */
function LandingRedirect(): ReactElement | null {
  const me = useMe();
  useEffect(() => {
    if (me.loading || !me.user || typeof window === "undefined") return;
    const path = window.location.pathname.replace(/\/+$/, "");
    const onDash = path === "/app" || path === "/app/dashboard";
    if (onDash && !me.can("dashboard.view")) {
      const target = LANDING_ORDER.find((k) => {
        const perm = MODULE_PERMISSION[k];
        // Only land on a module that is both released and permitted.
        return perm !== null && isModuleAvailable(k) && me.can(perm);
      });
      const mod = target ? CONSOLE_MODULES.find((m) => m.key === target) : undefined;
      if (mod && mod.path !== path) window.location.assign(mod.path);
    }
  }, [me.loading, me.user, me]);
  return null;
}

export default ConsoleShell;
